import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { InputFieldMaterialComponent } from '../../../../shared/components/input-field-material/input-field-material.component';
import { SelectMaterialComponent } from '../../../../shared/components/select-material/select-material.component';
import { TextAreaMaterialComponent } from '../../../../shared/components/text-area-material/text-area-material.component';
import { ModalFormService } from '../../../../shared/components/modal-form/service/modal-form.service';
import { TeledeteccionService } from '../../services/teledeteccion.service';
import {
  CreateAreaRequest,
  TeledeteccionArea,
} from '../../../../model/teledeteccion-area.model';

@Component({
  selector: 'app-form-crear-area',
  templateUrl: './form-crear-area.component.html',
  styleUrl: './form-crear-area.component.scss',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputFieldMaterialComponent,
    SelectMaterialComponent,
    TextAreaMaterialComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormCrearAreaComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  areaForm: FormGroup;
  isValidForm = signal(false);
  isSuccess = signal(false);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  successChange = output<boolean>();
  areaCreated = output<TeledeteccionArea | Object | undefined>(); // Output para emitir cuando se crea un área
  submitError = output<string | string[] | null>();

  // Injección del servicio usando inject()
  private teledeteccionService = inject(TeledeteccionService);

  // Lista de cultivos disponibles
  list_cultivos: { value: string; viewValue: string }[] = [
    { value: 'citricos', viewValue: 'Cítricos' },
    { value: 'cereales', viewValue: 'Cereales' },
    { value: 'hortalizas', viewValue: 'Hortalizas' },
    { value: 'frutales', viewValue: 'Frutales' },
    { value: 'olivo', viewValue: 'Olivo' },
    { value: 'vid', viewValue: 'Vid' },
    { value: 'almendro', viewValue: 'Almendro' },
    { value: 'arroz', viewValue: 'Arroz' },
    { value: 'otros', viewValue: 'Otros' },
  ];

  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private modalFormService: ModalFormService
  ) {
    effect(() => {
      this.successChange.emit(this.isSuccess());
    });

    // Effect para monitorear el estado del servicio
    effect(() => {
      this.isLoading.set(this.teledeteccionService.loading());
      this.errorMessage.set(this.teledeteccionService.error());
    });

    this.initializeForm();
  }

  ngOnInit(): void {
    // Aquí puedes agregar lógica de inicialización si es necesaria
  }

  ngAfterViewInit(): void {
    this.areaForm.valueChanges.subscribe(() => {
      this.isValidForm.set(this.areaForm.valid);
    });
  }

  ngOnDestroy(): void {
    // Limpiar suscripciones
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeForm(): void {
    this.areaForm = this.fb.group({
      nombreReferencia: new FormControl('', [
        Validators.required,
        Validators.maxLength(100),
      ]),
      informacionExtra: new FormControl('', [Validators.maxLength(500)]),
      cultivos: new FormControl(null),
    });
  }

  // Getters para fácil acceso a los controles del formulario
  get nombreReferencia(): FormControl {
    return this.areaForm.get('nombreReferencia') as FormControl;
  }

  get informacionExtra(): FormControl {
    return this.areaForm.get('informacionExtra') as FormControl;
  }

  get cultivos(): FormControl {
    return this.areaForm.get('cultivos') as FormControl;
  }

  onSubmit(): void {
    if (this.areaForm.valid) {
      this.isLoading.set(true);

      // Limpiar errores previos
      this.teledeteccionService.clearError();
      this.errorMessage.set(null);

      // Preparar los datos sin geojson (se toma del servicio)
      const areaData: Omit<CreateAreaRequest, 'geojson'> = {
        name: this.nombreReferencia.value,
        comment: this.informacionExtra.value || '',
        crop: this.cultivos.value,
      };

      console.log('📝 Datos del formulario:', areaData);
      console.log(
        '🗺️ GeoJSON del servicio:',
        this.teledeteccionService.creatingArea()
      );

      // Crear el área usando el servicio
      this.teledeteccionService.createArea(areaData).subscribe({
        next: response => {
          console.log('✅ Área creada exitosamente:', response);
          this.isLoading.set(false);
          this.isSuccess.set(true);
          this.resetForm();

          // Validar que la respuesta contiene el área
          if (response && response.area) {
            console.log('📍 Emitiendo área creada:', response.area);
            this.areaCreated.emit(response.area);
          } else {
            // Aún así podemos emitir un evento indicando que se creó algo
            // pero sin el área completa
            this.areaCreated.emit(undefined);
          }
        },
        error: error => {
          this.isLoading.set(false);
          // Siempre emitimos el error del backend al modal, independientemente del status (400, 422, etc.)
          const detail = error?.error?.detail ?? error?.detail;
          if (detail) {
            this.submitError.emit(detail);
          } else if (error?.error?.message) {
            this.submitError.emit(error.error.message);
          } else if (error?.message) {
            this.submitError.emit(error.message);
          } else {
            this.submitError.emit('Error al crear el área');
          }

          // Mostrar mensaje de error al usuario
          this.showErrorMessage(error);
        },
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  /**
   * Resetear el formulario
   */
  private resetForm(): void {
    this.areaForm.reset();
    this.isValidForm.set(false);
  }

  /**
   * Marcar todos los campos como touched
   */
  private markFormGroupTouched(): void {
    this.areaForm.markAllAsTouched();
  }

  /**
   * Mostrar mensaje de error al usuario
   */
  private showErrorMessage(error: any): void {
    // Normalizamos posibles estructuras de error del backend
    const normalized = this.normalizeBackendError(error);
    this.errorMessage.set(normalized);
  }

  /**
   * Normaliza errores del backend a un string legible
   * - Si viene detail como array: une por saltos de línea
   * - Si viene detail como string: devuelve tal cual
   * - Si viene message: usa message
   * - Fallback genérico
   */
  private normalizeBackendError(error: any): string {
    const detail = error?.error?.detail ?? error?.detail;
    if (Array.isArray(detail)) {
      return detail.filter(Boolean).join('\n');
    }
    if (typeof detail === 'string' && detail.trim().length > 0) {
      return detail;
    }
    if (typeof error?.error?.message === 'string') {
      return error.error.message;
    }
    if (typeof error?.message === 'string') {
      return error.message;
    }
    return 'Error al crear el área';
  }

  /**
   * Cerrar el modal
   */
  private closeModal(): void {
    this.modalFormService.showingModal.next(false);
    this.modalFormService.closeDrawer.next(true);
  }

  /**
   * Ejemplo de uso: Cargar todas las áreas
   */
  loadAllAreas(): void {
    this.teledeteccionService.getAllAreas().subscribe({
      next: response => {
        console.log('Áreas cargadas:', response);
      },
      error: error => {
        console.error('Error al cargar áreas:', error);
      },
    });
  }

  /**
   * Ejemplo de uso: Obtener un área específica por ID
   */
  loadAreaById(areaId: number): void {
    this.teledeteccionService.getAreaById(areaId).subscribe({
      next: response => {
        console.log('Área obtenida:', response.area);
      },
      error: error => {
        console.error('Error al obtener área:', error);
      },
    });
  }

  /**
   * Ejemplo de uso: Actualizar un área
   */
  updateArea(areaId: number): void {
    const updateData = {
      name: 'Área actualizada',
      comment: 'Comentario actualizado',
      crop: 'citricos',
    };

    this.teledeteccionService.updateArea(areaId, updateData).subscribe({
      next: response => {
        console.log('Área actualizada:', response.area);
      },
      error: error => {
        console.error('Error al actualizar área:', error);
      },
    });
  }

  /**
   * Ejemplo de uso: Eliminar un área
   */
  deleteArea(areaId: number): void {
    if (confirm('¿Estás seguro de que quieres eliminar esta área?')) {
      this.teledeteccionService.deleteArea(areaId).subscribe({
        next: response => {
          console.log('Área eliminada:', response);
        },
        error: error => {
          console.error('Error al eliminar área:', error);
        },
      });
    }
  }

  onCancel(): void {
    this.closeModal();
  }
}
